import React from "react";
import {css} from "@emotion/react";

import {useIsMobile} from "@pg-mono/hooks";

import {getLinearScaleFunction} from "../helpers/linearScale";
import {QuartileBarSVGText} from "./QuartileBarSVGText";

const SecondQuartileDashedLinePath = (props: {dotDistanceFromLeftPX: number}) => (
    <path css={transformFromOriginalPosition(props)} d="M13 -10V46" stroke="#DCDCDD" strokeDasharray="5 5" />
);

const AvgPriceDotPath = (props: {dotDistanceFromLeftPX: number}) => (
    <path
        css={transformFromOriginalPosition(props)}
        d="M13 76C20.1797 76 26 70.1797 26 63C26 55.8203 20.1797 50 13 50C5.8203 50 0 55.8203 0 63C0 70.1797 5.8203 76 13 76Z"
        fill="#D7AFE1"
        stroke="white"
        strokeWidth="2"
    />
);

interface IAvgPriceDotPathProps {
    dotDistanceFromLeftPX: number;
}

const transformFromOriginalPosition = (props: IAvgPriceDotPathProps) => css`
    transform: translate(${props.dotDistanceFromLeftPX + "px"}, 0);
`;

const DesktopBarSVG = (props: {dotDistanceFromLeftPX: number}) => {
    return (
        <svg css={quartileBarImage} width="396" height="89" viewBox="0 0 396 89" fill="none" xmlns="http://www.w3.org/2000/svg">
            <g clipPath="url(#clip0)">
                <path
                    d="M387 52H9C4.02944 52 0 56.0294 0 61C0 65.9706 4.02944 70 9 70H387C391.971 70 396 65.9706 396 61C396 56.0294 391.971 52 387 52Z"
                    fill="#DCDCDD"
                />
                <path
                    d="M288 52H107C102.029 52 98 56.0294 98 61C98 65.9706 102.029 70 107 70H288C292.971 70 297 65.9706 297 61C297 56.0294 292.971 52 288 52Z"
                    fill="url(#paint0_linear)"
                />
                <path d="M97.5 33V89" stroke="#DCDCDD" strokeDasharray="5 5" />
                <path d="M297.5 33V89" stroke="#DCDCDD" strokeDasharray="5 5" />
                <AvgPriceDotPath dotDistanceFromLeftPX={props.dotDistanceFromLeftPX} />
                <SecondQuartileDashedLinePath dotDistanceFromLeftPX={props.dotDistanceFromLeftPX} />
            </g>
            <defs>
                <linearGradient id="paint0_linear" x1="297" y1="51.9999" x2="97.6061" y2="59.4321" gradientUnits="userSpaceOnUse">
                    <stop stopColor="#D7AFE1" />
                    <stop offset="1" stopColor="#D7AFE1" />
                </linearGradient>
                <clipPath id="clip0">
                    <rect width="396" height="89" fill="white" />
                </clipPath>
            </defs>
        </svg>
    );
};

const MobileBarSVG = (props: {dotDistanceFromLeftPX: number}) => {
    return (
        <svg css={quartileBarImage} width="279" height="93" viewBox="0 0 279 93" fill="none" xmlns="http://www.w3.org/2000/svg">
            <g clipPath="url(#clip0)">
                <path
                    d="M270 56H9C4.02944 56 0 60.0294 0 65C0 69.9706 4.02944 74 9 74H270C274.971 74 279 69.9706 279 65C279 60.0294 274.971 56 270 56Z"
                    fill="#DCDCDD"
                />
                <path
                    d="M200 56H78C73.0294 56 69 60.0294 69 65C69 69.9706 73.0294 74 78 74H200C204.971 74 209 69.9706 209 65C209 60.0294 204.971 56 200 56Z"
                    fill="url(#paint0_linear)"
                />
                <path d="M69.5 37V93" stroke="#DCDCDD" strokeDasharray="5 5" />
                <path d="M209.5 37V93" stroke="#DCDCDD" strokeDasharray="5 5" />
                <AvgPriceDotPath dotDistanceFromLeftPX={props.dotDistanceFromLeftPX} />
                <SecondQuartileDashedLinePath dotDistanceFromLeftPX={props.dotDistanceFromLeftPX} />
            </g>
            <defs>
                <linearGradient id="paint0_linear" x1="209" y1="55.9999" x2="68.6245" y2="59.6809" gradientUnits="userSpaceOnUse">
                    <stop stopColor="#D7AFE1" />
                    <stop offset="1" stopColor="#D7AFE1" />
                </linearGradient>
                <clipPath id="clip0">
                    <rect width="279" height="93" fill="white" />
                </clipPath>
            </defs>
        </svg>
    );
};

export interface IQuartileBarProps {
    firstQuartile: number;
    secondQuartile: number;
    thirdQuartile: number;
    avgPrice: number;
    dotDistanceFromLeftPX: number;
}

interface IQuartileBarSVGProps {
    firstQuartile: number;
    secondQuartile: number;
    thirdQuartile: number;
    avgPrice: number;
}

export const QuartileBarSVG = (props: IQuartileBarSVGProps) => {
    const isMobile = useIsMobile();

    const greenBarWidthPX = isMobile ? 164 : 194.5;
    const wholeBarWidthPX = isMobile ? 253 : 370;
    const firstQuartilePositionFromLeftPX = isMobile ? 43 : 88;
    const lastQuartilePositionFromLeftPX = greenBarWidthPX + firstQuartilePositionFromLeftPX;
    const chartDomain: [number, number] = [props.firstQuartile, props.thirdQuartile];
    const chartRange: [number, number] = [firstQuartilePositionFromLeftPX, lastQuartilePositionFromLeftPX];

    const scale = getLinearScaleFunction(chartDomain, chartRange, [-1, wholeBarWidthPX]);
    const avgDotPositionFromFirstQuartileInPX = scale(props.avgPrice);

    return (
        <div css={quartileBarHolder(isMobile)}>
            <QuartileBarSVGText
                firstQuartile={props.firstQuartile}
                secondQuartile={props.avgPrice}
                thirdQuartile={props.thirdQuartile}
                avgPrice={props.avgPrice}
                wholeBarWidthPX={wholeBarWidthPX}
                dotDistanceFromLeftPX={avgDotPositionFromFirstQuartileInPX}
            />
            {isMobile ? (
                <MobileBarSVG dotDistanceFromLeftPX={avgDotPositionFromFirstQuartileInPX} />
            ) : (
                <DesktopBarSVG dotDistanceFromLeftPX={avgDotPositionFromFirstQuartileInPX} />
            )}
        </div>
    );
};

const quartileBarImage = css`
    display: block;
    font-size: 1.4rem;
    max-width: 100%;
    width: 100%;
`;

const quartileBarHolder = (isMobile: boolean) => css`
    position: relative;
    padding-top: 3.1rem;
    padding-bottom: 2rem;
    margin-left: auto;
    margin-right: auto;
    max-width: ${isMobile ? "28.1rem" : "39.6rem"};
`;
import React from "react";
import {css} from "@emotion/react";

import {useIsMobile} from "@pg-mono/hooks";

import {getLinearScaleFunction} from "../helpers/linearScale";
import {QuartileBarSVGText} from "./QuartileBarSVGText";

const SecondQuartileDashedLinePath = (props: {dotDistanceFromLeftPX: number}) => (
    <path css={transformFromOriginalPosition(props)} d="M13 -10V46" stroke="#DCDCDD" strokeDasharray="5 5" />
);

const AvgPriceDotPath = (props: {dotDistanceFromLeftPX: number}) => (
    <path
        css={transformFromOriginalPosition(props)}
        d="M13 76C20.1797 76 26 70.1797 26 63C26 55.8203 20.1797 50 13 50C5.8203 50 0 55.8203 0 63C0 70.1797 5.8203 76 13 76Z"
        fill="#D7AFE1"
        stroke="white"
        strokeWidth="2"
    />
);

interface IAvgPriceDotPathProps {
    dotDistanceFromLeftPX: number;
}

const transformFromOriginalPosition = (props: IAvgPriceDotPathProps) => css`
    transform: translate(${props.dotDistanceFromLeftPX + "px"}, 0);
`;

const DesktopBarSVG = (props: {dotDistanceFromLeftPX: number}) => {
    return (
        <svg css={quartileBarImage} width="396" height="89" viewBox="0 0 396 89" fill="none" xmlns="http://www.w3.org/2000/svg">
            <g clipPath="url(#clip0)">
                <path
                    d="M387 52H9C4.02944 52 0 56.0294 0 61C0 65.9706 4.02944 70 9 70H387C391.971 70 396 65.9706 396 61C396 56.0294 391.971 52 387 52Z"
                    fill="#DCDCDD"
                />
                <path
                    d="M288 52H107C102.029 52 98 56.0294 98 61C98 65.9706 102.029 70 107 70H288C292.971 70 297 65.9706 297 61C297 56.0294 292.971 52 288 52Z"
                    fill="url(#paint0_linear)"
                />
                <path d="M97.5 33V89" stroke="#DCDCDD" strokeDasharray="5 5" />
                <path d="M297.5 33V89" stroke="#DCDCDD" strokeDasharray="5 5" />
                <AvgPriceDotPath dotDistanceFromLeftPX={props.dotDistanceFromLeftPX} />
                <SecondQuartileDashedLinePath dotDistanceFromLeftPX={props.dotDistanceFromLeftPX} />
            </g>
            <defs>
                <linearGradient id="paint0_linear" x1="297" y1="51.9999" x2="97.6061" y2="59.4321" gradientUnits="userSpaceOnUse">
                    <stop stopColor="#D7AFE1" />
                    <stop offset="1" stopColor="#D7AFE1" />
                </linearGradient>
                <clipPath id="clip0">
                    <rect width="396" height="89" fill="white" />
                </clipPath>
            </defs>
        </svg>
    );
};

const MobileBarSVG = (props: {dotDistanceFromLeftPX: number}) => {
    return (
        <svg css={quartileBarImage} width="279" height="93" viewBox="0 0 279 93" fill="none" xmlns="http://www.w3.org/2000/svg">
            <g clipPath="url(#clip0)">
                <path
                    d="M270 56H9C4.02944 56 0 60.0294 0 65C0 69.9706 4.02944 74 9 74H270C274.971 74 279 69.9706 279 65C279 60.0294 274.971 56 270 56Z"
                    fill="#DCDCDD"
                />
                <path
                    d="M200 56H78C73.0294 56 69 60.0294 69 65C69 69.9706 73.0294 74 78 74H200C204.971 74 209 69.9706 209 65C209 60.0294 204.971 56 200 56Z"
                    fill="url(#paint0_linear)"
                />
                <path d="M69.5 37V93" stroke="#DCDCDD" strokeDasharray="5 5" />
                <path d="M209.5 37V93" stroke="#DCDCDD" strokeDasharray="5 5" />
                <AvgPriceDotPath dotDistanceFromLeftPX={props.dotDistanceFromLeftPX} />
                <SecondQuartileDashedLinePath dotDistanceFromLeftPX={props.dotDistanceFromLeftPX} />
            </g>
            <defs>
                <linearGradient id="paint0_linear" x1="209" y1="55.9999" x2="68.6245" y2="59.6809" gradientUnits="userSpaceOnUse">
                    <stop stopColor="#D7AFE1" />
                    <stop offset="1" stopColor="#D7AFE1" />
                </linearGradient>
                <clipPath id="clip0">
                    <rect width="279" height="93" fill="white" />
                </clipPath>
            </defs>
        </svg>
    );
};

export interface IQuartileBarProps {
    firstQuartile: number;
    secondQuartile: number;
    thirdQuartile: number;
    avgPrice: number;
    dotDistanceFromLeftPX: number;
}

interface IQuartileBarSVGProps {
    firstQuartile: number;
    secondQuartile: number;
    thirdQuartile: number;
    avgPrice: number;
}

export const QuartileBarSVG = (props: IQuartileBarSVGProps) => {
    const isMobile = useIsMobile();

    const greenBarWidthPX = isMobile ? 164 : 194.5;
    const wholeBarWidthPX = isMobile ? 253 : 370;
    const firstQuartilePositionFromLeftPX = isMobile ? 43 : 88;
    const lastQuartilePositionFromLeftPX = greenBarWidthPX + firstQuartilePositionFromLeftPX;
    const chartDomain: [number, number] = [props.firstQuartile, props.thirdQuartile];
    const chartRange: [number, number] = [firstQuartilePositionFromLeftPX, lastQuartilePositionFromLeftPX];

    const scale = getLinearScaleFunction(chartDomain, chartRange, [-1, wholeBarWidthPX]);
    const avgDotPositionFromFirstQuartileInPX = scale(props.avgPrice);

    return (
        <div css={quartileBarHolder(isMobile)}>
            <QuartileBarSVGText
                firstQuartile={props.firstQuartile}
                secondQuartile={props.avgPrice}
                thirdQuartile={props.thirdQuartile}
                avgPrice={props.avgPrice}
                wholeBarWidthPX={wholeBarWidthPX}
                dotDistanceFromLeftPX={avgDotPositionFromFirstQuartileInPX}
            />
            {isMobile ? (
                <MobileBarSVG dotDistanceFromLeftPX={avgDotPositionFromFirstQuartileInPX} />
            ) : (
                <DesktopBarSVG dotDistanceFromLeftPX={avgDotPositionFromFirstQuartileInPX} />
            )}
        </div>
    );
};

const quartileBarImage = css`
    display: block;
    font-size: 1.4rem;
    max-width: 100%;
    width: 100%;
`;

const quartileBarHolder = (isMobile: boolean) => css`
    position: relative;
    padding-top: 3.1rem;
    padding-bottom: 2rem;
    margin-left: auto;
    margin-right: auto;
    max-width: ${isMobile ? "28.1rem" : "39.6rem"};
`;
